import { RefObject } from 'react';

import { clamp, getNumericOption, getQuantity } from '../utils';
import { WheelData } from '../WheelOfFortune/types';
import { WHEEL_PALETTES, WheelPalette } from '../wheelTheme';

export interface DrawWheelProps {
  fontWeight: number | string;
  fontSize: number;
  fontStyle: string;
  prizeMap: number[][];
  rouletteUpdater: boolean;
  textDistance: number;
  prizeNumber?: number;
  hasStoppedSpinning: boolean;
  hasStartedSpinning: boolean;
  /** Defaults to the regular wheel so existing callers keep their colours. */
  palette?: WheelPalette;
}

export const drawWheel = (
  canvasRef: RefObject<HTMLCanvasElement | null>,
  data: WheelData[],
  drawWheelProps: DrawWheelProps
) => {
  const { fontWeight, fontSize, fontStyle, prizeMap, textDistance } = drawWheelProps;
  const palette = drawWheelProps.palette ?? WHEEL_PALETTES.regular;
  const QUANTITY = getQuantity(prizeMap);
  const canvas = canvasRef.current;

  if (canvas?.getContext('2d')) {
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = 'transparent';
    ctx.lineWidth = 0;

    let startAngle = 0;
    const outsideRadius = canvas.width / 2 - 10;
    const clampedContentDistance = clamp(0, 100, textDistance);
    const contentRadius = (outsideRadius * clampedContentDistance) / 100;
    const clampedInsideRadius = clamp(0, 100, 0);
    const insideRadius = (outsideRadius * clampedInsideRadius) / 100;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // Store selected segment angles to draw border at the end
    let selectedStartAngle: number | null = null;
    let selectedEndAngle: number | null = null;

    for (let i = 0; i < data.length; i++) {
      const { optionSize, textColor, option, image } = data[i];
      const arc = (optionSize && (optionSize * (2 * Math.PI)) / QUANTITY) || (2 * Math.PI) / QUANTITY;
      const endAngle = startAngle + arc;

      // Store angles for selected segment (only if not spinning)
      if (drawWheelProps.hasStoppedSpinning && !drawWheelProps.hasStartedSpinning && drawWheelProps.prizeNumber === i) {
        selectedStartAngle = startAngle;
        selectedEndAngle = endAngle;
      }

      // Create segment gradient
      const segmentGradient = ctx.createLinearGradient(
        centerX,
        centerY,
        centerX + Math.cos(startAngle) * outsideRadius,
        centerY + Math.sin(startAngle) * outsideRadius
      );

      const [stop0, stop1, stop2] = i % 2 === 0 ? palette.segmentA : palette.segmentB;
      segmentGradient.addColorStop(0, stop0);
      segmentGradient.addColorStop(0.5, stop1);
      segmentGradient.addColorStop(1, stop2);

      ctx.fillStyle = segmentGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, outsideRadius, startAngle, endAngle, false);
      ctx.arc(centerX, centerY, insideRadius, endAngle, startAngle, true);
      ctx.stroke();
      ctx.fill();

      ctx.save();

      // CONTENT FILL
      ctx.translate(
        centerX + Math.cos(startAngle + arc / 2) * contentRadius,
        centerY + Math.sin(startAngle + arc / 2) * contentRadius
      );
      let contentRotationAngle = startAngle + arc / 2;

      // DRAW IMAGE
      if (image) {
        contentRotationAngle += Math.PI / 2;
        ctx.rotate(contentRotationAngle);

        const img = image?._imageHTML || new Image();
        ctx.drawImage(img, img.width / -2, -(img.height - 90 + image?.offset!) / 2, img.width, img.height);
        ctx.rotate(-contentRotationAngle);
      }

      // DRAW TEXT
      ctx.rotate(contentRotationAngle);
      const text = getNumericOption(option);
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize * 2}px Rajdhani`;
      ctx.fillStyle = textColor as string;
      ctx.fillText(text || '', -ctx.measureText(text || '').width / 2, fontSize / 2.7);
      ctx.restore();

      startAngle = endAngle;
    }

    // Draw the highlight border for the selected segment at the end (left and right edges
    // only). Palette-coloured: neon blue on the regular wheel, gold on the VIP one.
    // This ensures the border is drawn on top of all segments
    // Only show border when wheel has stopped and is not spinning
    if (
      drawWheelProps.hasStoppedSpinning &&
      !drawWheelProps.hasStartedSpinning &&
      selectedStartAngle !== null &&
      selectedEndAngle !== null
    ) {
      ctx.strokeStyle = palette.highlight;
      ctx.lineWidth = 6;
      ctx.shadowColor = palette.highlight;
      ctx.shadowBlur = 15;

      // Draw left radial line
      ctx.beginPath();
      ctx.moveTo(
        centerX + Math.cos(selectedStartAngle) * insideRadius,
        centerY + Math.sin(selectedStartAngle) * insideRadius
      );
      ctx.lineTo(
        centerX + Math.cos(selectedStartAngle) * outsideRadius,
        centerY + Math.sin(selectedStartAngle) * outsideRadius
      );
      ctx.stroke();

      // Draw right radial line
      ctx.beginPath();
      ctx.moveTo(
        centerX + Math.cos(selectedEndAngle) * insideRadius,
        centerY + Math.sin(selectedEndAngle) * insideRadius
      );
      ctx.lineTo(
        centerX + Math.cos(selectedEndAngle) * outsideRadius,
        centerY + Math.sin(selectedEndAngle) * outsideRadius
      );
      ctx.stroke();

      // Reset shadow and stroke style
      ctx.shadowBlur = 0;
      ctx.strokeStyle = 'transparent';
      ctx.lineWidth = 0;
    }
  }
};
